import React, { useState, useEffect, useCallback } from "react"
import { Col, Row, Form } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { serialize } from "object-to-formdata"

// layouts
import { ContentActionButtons } from "layoutsV2/pro/PersonalInfoLayout/ContentActionButtons"
import { useSaveContext } from "layoutsV2/pro/PersonalInfoLayout/SaveContext"
// components
import { AddressesHeader } from "./Header"
import { AddressesSearch } from "./Search"
import { CustomAddress } from "./CustomAddress"
import { ProfilePreview } from "ui/PersonalInfo/ProfilePreview"
import MapPinPicker from "./MapPinPicker/MapPinPicker"
import styles from "./Addresses.module.css"
import { extractAddressFields } from "./addressUtils"
// store
import { guideSelector, updateGuide } from "store/user"

export const PersonalInfoAddresses = () => {
  const [manualAddressEnabled, setManualAddressEnabled] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [mapSelectedAddress, setMapSelectedAddress] = useState(null)
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id, street_address, state, country, city, zip_code, latitude, longitude, ...guide } = useSelector(guideSelector)
  const [mapCoords, setMapCoords] = useState(latitude && longitude ? { lat: Number(latitude), lng: Number(longitude) } : null)
  const { registerSaveCallback, unregisterSaveCallback } = useSaveContext()

  const initialValues = {
    street_address,
    state,
    country,
    city,
    zip_code,
    latitude: latitude ?? null,
    longitude: longitude ?? null
  }

  const onFinish = (formData, callback) => {
    const serializedFormData = serialize({ guide: formData }, { indices: true })
    dispatch(updateGuide(id, serializedFormData)).then(callback)
  }

  const saveFormCallback = useCallback(
    (onComplete) => {
      if (form.isFieldsTouched()) {
        const values = form.getFieldsValue()
        onFinish(values, onComplete)
      } else {
        if (onComplete) onComplete()
      }
    },
    [form, onFinish]
  )

  useEffect(() => {
    registerSaveCallback(saveFormCallback)
    return () => {
      unregisterSaveCallback()
    }
  }, [registerSaveCallback, unregisterSaveCallback, saveFormCallback])

  const handlePreviewToggle = () => setShowPreview((prev) => !prev)

  const handleMapLocationChange = useCallback(
    ({ lat, lng, formattedAddress, addressComponents }) => {
      form.setFieldsValue({ latitude: lat, longitude: lng })
      setMapCoords({ lat, lng })
      if (formattedAddress) setMapSelectedAddress(formattedAddress)
      if (!addressComponents) return

      const addressFields = extractAddressFields(addressComponents)
      form.setFields([
        { name: "street_address", value: addressFields.street_address, touched: true, errors: [] },
        { name: "city", value: addressFields.city, touched: true, errors: [] },
        { name: "state", value: addressFields.state, touched: true, errors: [] },
        { name: "zip_code", value: addressFields.zip_code, touched: true, errors: [] },
        { name: "country", value: addressFields.country, touched: true, errors: [] },
        { name: "latitude", value: lat, touched: true, errors: [] },
        { name: "longitude", value: lng, touched: true, errors: [] }
      ])
      
      // Trigger validation to clear any errors
      form.validateFields(['street_address', 'city', 'state', 'zip_code', 'country']).catch(() => {})
    },
    [form]
  )

  const handleSetCenterLatLng = useCallback(
    (coords) => {
      const lat = coords?.lat ?? null
      const lng = coords?.lng ?? null
      form.setFields([
        { name: "latitude", value: lat, touched: true },
        { name: "longitude", value: lng, touched: true }
      ])
      setMapCoords(lat && lng ? { lat, lng } : null)
    },
    [form]
  )

  return (
    <>
      <Row>
        <Col lg={16} md={24} xs={24}>
          <AddressesHeader onPreview={handlePreviewToggle} />
          {manualAddressEnabled ? (
            <CustomAddress
              backToSearch={() => setManualAddressEnabled(false)}
              form={form}
              initialValues={initialValues}
              setCenterLatLng={handleSetCenterLatLng}
              setMeetingLatLng={setMapSelectedAddress}
            />
          ) : (
            <AddressesSearch
              setManualAddressEnabled={() => setManualAddressEnabled(true)}
              form={form}
              initialValues={initialValues}
              externalAddress={mapSelectedAddress}
              onCoordsChange={handleSetCenterLatLng}
              onFinish={onFinish}
            />
          )}
          <div className={styles.mapWrapper}>
            <MapPinPicker latitude={mapCoords?.lat} longitude={mapCoords?.lng} onLocationChange={handleMapLocationChange} />
          </div>
        </Col>
        <Col xs={24}>
          <ContentActionButtons form={form} onFinish={onFinish} />
        </Col>
      </Row>
      <ProfilePreview
        show={showPreview}
        tab="about"
        guide={{
          ...guide,
          display_location: `${form.getFieldValue("city")}, ${form.getFieldValue("country")}`
        }}
        onClose={handlePreviewToggle}
      />
    </>
  )
}
